import { useEffect } from 'react';
import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';

export const CALENDLY_URL = 'https://calendly.com/zach-lotenlabs/new-meeting';
export const QUOTE_MAILTO =
  'mailto:zach@lotenlabs.com?subject=Loten%20Labs%20Quote%20Request&body=Game%20name%3A%20%0AEstimated%20word%20count%3A%20%0ATarget%20languages%3A%20%0ATarget%20ship%20date%3A%20';

export const WORKFLOW_STEPS = [
  {
    number: '01',
    label: 'Upload strings',
    title: 'Your repo is the source of truth',
    description: 'We sync straight to GitHub, Perforce, or UVC. New commits flow in on their own.',
    visual: 'repo',
  },
  {
    number: '02',
    label: 'Capture context',
    title: 'See the game the way players will',
    description: 'Drop in gameplay footage. Our plugin links every string to the frame it appears in.',
    visual: 'video',
  },
  {
    number: '03',
    label: 'Style guide and first pass',
    title: 'Characters, tone, glossary. Locked in.',
    description: 'We build a style guide for your game from the captured context, then draft every language against it.',
    visual: 'context',
  },
  {
    number: '04',
    label: 'Linguist review',
    title: 'Native speakers who play games',
    description: 'Every line goes to a native-speaking reviewer in your genre. They see the scene and rewrite whatever misses.',
    visual: 'review',
  },
  {
    number: '05',
    label: 'QA and merge',
    title: 'Localization stops blocking your release',
    description: 'A final consistency pass, then a pull request back to your repo. Merge and ship.',
    visual: 'pr',
  },
] as const;

export const PRICING_INCLUDES = [
  'Gameplay context for every string',
  'A style guide built from your game',
  'Native-speaking linguist review',
  'Direct repo integration',
  '14-day turnaround. Rush available.',
] as const;

export const PRICING_EXCLUDES = [
  'Per-seat licensing',
  'Setup fees',
  'A producer-month of coordination',
  'Spreadsheets',
] as const;

export const FAQS = [
  {
    q: 'How do you handle strings that reference unreleased IP?',
    a: 'We sign MSAs and NDAs before any ingestion. Your footage and strings sit behind per-project access controls, and your IP is never shared, sold, or used to train external systems.',
  },
  {
    q: "What if we don't have gameplay footage yet?",
    a: 'Ship us a build, screenshots, or static UI mockups. The more context we have, the better the translation lands. We never block a project on footage availability.',
  },
  {
    q: 'How do you handle patch cycles and live-ops string drops?',
    a: 'Default turnaround is 14 days from string ingestion to an approved pull request. Incremental string drops from patches and content updates flow through the same pipeline, mapped against the style guide we already built for your game. Rush turnaround is available when a trailer or launch window demands it.',
  },
  {
    q: 'What languages do you support?',
    a: '30+ languages including Simplified Chinese, Traditional Chinese, Japanese, Korean, Spanish (LatAm and EU), Brazilian Portuguese, French, German, Italian, Polish, Russian, and Arabic. Full list on request.',
  },
  {
    q: 'Who are your linguists?',
    a: 'Native speakers who play games in the genres they review. RPGs go to RPG players. Tactics games go to tactics players.',
  },
  {
    q: 'What engines and workflows do you work with?',
    a: 'Native support for Unity, Unreal, and Godot. We sync directly with GitHub, Perforce, and UVC. If your setup is custom, we integrate with anything that has a file format and a pull-request concept.',
  },
  {
    q: 'How is this different from a traditional localization agency?',
    a: 'Agencies hand your producer a spreadsheet and quote in opaque tiers with setup fees and per-language minimums. We work directly from your repo and your gameplay footage at a flat $0.10 per word. Agencies take four to six weeks and need a coordinator on your side. We take fourteen days and we are the coordinator.',
  },
  {
    q: 'Can you work around our milestone calendar?',
    a: 'Yes. Most of our studios plan ingestion around content locks and patch cadences. Tell us when strings freeze and when the pull request has to land, and we schedule the linguist review around your milestone, not ours.',
  },
] as const;

function StepVisual({ type }: { type: string }): JSX.Element {
  switch (type) {
    case 'repo':
      return (
        <div className="step-visual step-visual--repo">
          <div className="sv-window">
            <div className="sv-window__bar">
              <span className="sv-dot" /><span className="sv-dot" /><span className="sv-dot" />
            </div>
            <div className="sv-window__body">
              <div className="sv-file"><span className="sv-file__icon">📁</span> en/strings.json</div>
              <div className="sv-file"><span className="sv-file__icon">📁</span> en/ui_text.json</div>
              <div className="sv-file sv-file--active"><span className="sv-file__icon">📄</span> en/dialogue.json</div>
              <div className="sv-file"><span className="sv-file__icon">📁</span> en/items.json</div>
            </div>
          </div>
        </div>
      );
    case 'video':
      return (
        <div className="step-visual step-visual--video">
          <div className="sv-player">
            <div className="sv-player__screen">
              <div className="sv-player__play">▶</div>
              <div className="sv-player__label">playtest_build_v3.mp4</div>
            </div>
            <div className="sv-player__timeline">
              <div className="sv-player__progress" />
              <div className="sv-player__markers">
                <span className="sv-marker" style={{ left: '15%' }} />
                <span className="sv-marker" style={{ left: '35%' }} />
                <span className="sv-marker sv-marker--active" style={{ left: '52%' }} />
                <span className="sv-marker" style={{ left: '78%' }} />
              </div>
            </div>
          </div>
        </div>
      );
    case 'context':
      return (
        <div className="step-visual step-visual--context">
          <div className="sv-split">
            <div className="sv-split__left">
              <div className="sv-split__label"><span className="sv-split__icon">▶</span> Gameplay Frame</div>
              <div className="sv-frame">
                <div className="sv-bbox sv-bbox--1">
                  <span className="sv-bbox__label">menu_title</span>
                </div>
                <div className="sv-bbox sv-bbox--2">
                  <span className="sv-bbox__label">btn_start</span>
                </div>
                <div className="sv-bbox sv-bbox--3">
                  <span className="sv-bbox__label">tooltip_hint</span>
                </div>
              </div>
            </div>
            <div className="sv-split__connector">
              <svg width="24" height="80" viewBox="0 0 24 80"><path d="M12 0v25M2 25l10 10 10-10M12 35v10M12 45v25M2 45l10 10 10-10" stroke="var(--primary-light)" strokeWidth="1.5" fill="none" opacity="0.5" /></svg>
            </div>
            <div className="sv-split__right">
              <div className="sv-split__label"><span className="sv-split__icon">{'{ }'}</span> String Map</div>
              <div className="sv-code">
                <div className="sv-code__line"><span className="sv-key">"menu_title"</span>: <span className="sv-val">"New Adventure"</span></div>
                <div className="sv-code__line sv-code__line--highlight"><span className="sv-key">"btn_start"</span>: <span className="sv-val">"Begin Quest"</span></div>
                <div className="sv-code__line"><span className="sv-key">"tooltip_hint"</span>: <span className="sv-val">"Press E to interact"</span></div>
              </div>
            </div>
          </div>
        </div>
      );
    case 'review':
      return (
        <div className="step-visual step-visual--review">
          <div className="sv-review">
            <div className="sv-review__row">
              <div className="sv-review__original">"Begin Quest"</div>
              <div className="sv-review__arrow">→</div>
              <div className="sv-review__translated">
                <span className="sv-lang">🇯🇵</span> "冒険を始める"
              </div>
              <span className="sv-review__status sv-review__status--approved">✓</span>
            </div>
            <div className="sv-review__row sv-review__row--active">
              <div className="sv-review__original">"Press E to interact"</div>
              <div className="sv-review__arrow">→</div>
              <div className="sv-review__translated">
                <span className="sv-lang">🇯🇵</span> "Eキーで操作"
              </div>
              <span className="sv-review__status sv-review__status--editing">✎</span>
            </div>
            <div className="sv-review__row">
              <div className="sv-review__original">"New Adventure"</div>
              <div className="sv-review__arrow">→</div>
              <div className="sv-review__translated">
                <span className="sv-lang">🇯🇵</span> "新たなる冒険"
              </div>
              <span className="sv-review__status sv-review__status--approved">✓</span>
            </div>
          </div>
        </div>
      );
    case 'pr':
      return (
        <div className="step-visual step-visual--pr">
          <div className="sv-pr">
            <div className="sv-pr__header">
              <span className="sv-pr__icon">⬤</span>
              <span className="sv-pr__title">Add Japanese localization (ja-JP)</span>
              <span className="sv-pr__badge">Ready to merge</span>
            </div>
            <div className="sv-pr__stats">
              <span className="sv-pr__add">+2,847 lines</span>
              <span className="sv-pr__files">14 files</span>
              <span className="sv-pr__langs">5 languages</span>
            </div>
            <div className="sv-pr__checks">
              <div className="sv-pr__check"><span className="sv-check-icon">✓</span> All strings context-verified</div>
              <div className="sv-pr__check"><span className="sv-check-icon">✓</span> Human review passed</div>
              <div className="sv-pr__check"><span className="sv-check-icon">✓</span> Glossary compliance: 100%</div>
            </div>
          </div>
        </div>
      );
    default:
      return <div className="step-visual" />;
  }
}

interface HeaderProps {
  onNavClick?: (sectionId: string) => void;
}

export function Header({ onNavClick }: HeaderProps): JSX.Element {
  const handle = (e: MouseEvent<HTMLAnchorElement>, anchor: string) => {
    if (onNavClick) {
      e.preventDefault();
      onNavClick(anchor);
    }
  };
  return (
    <header>
      <nav className="container">
        <Link to="/" className="logo">
          <img src="/mstile-150x150.png" alt="Loten Labs" className="logo-icon" />
          Loten Labs
        </Link>
        <ul className="nav-links">
          <li>
            <Link to="/how-it-works" onClick={(e) => handle(e, 'workflow')}>
              How It Works
            </Link>
          </li>
          <li>
            <Link to="/pricing" onClick={(e) => handle(e, 'pricing')}>
              Pricing
            </Link>
          </li>
          <li>
            <Link to="/faq" onClick={(e) => handle(e, 'faq')}>
              FAQ
            </Link>
          </li>
          <li>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              Contact
            </a>
          </li>
        </ul>
        <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="cta-button nav-cta">
          Book a Demo
        </a>
      </nav>
    </header>
  );
}

export function Footer(): JSX.Element {
  return (
    <footer>
      <div className="container footer-content">
        <div className="footer-brand">
          <img src="/mstile-150x150.png" alt="Loten Labs" className="footer-logo-icon" />
          <span className="footer-name">Loten Labs</span>
        </div>
        <div className="footer-legal">
          <Link to="/privacy-policy">Privacy Policy</Link>
          <span className="footer-legal__sep" aria-hidden="true">·</span>
          <Link to="/cookie-policy">Cookie Policy</Link>
          <span className="footer-legal__sep" aria-hidden="true">·</span>
          <Link to="/terms">Terms &amp; Conditions</Link>
          <span className="footer-legal__sep" aria-hidden="true">·</span>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" className="termly-display-preferences">Consent Preferences</a>
        </div>
        <p>© {new Date().getFullYear()} Loten Labs. All rights reserved.</p>
      </div>
    </footer>
  );
}

export function useRevealObserver(): void {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    const elements = document.querySelectorAll('.reveal');
    elements.forEach((el) => observer.observe(el));
    return () => elements.forEach((el) => observer.unobserve(el));
  }, []);
}

interface WorkflowProps {
  heading?: JSX.Element;
  subtitle?: string;
}

export function WorkflowSection({ heading, subtitle }: WorkflowProps): JSX.Element {
  return (
    <section id="workflow" className="workflow" aria-label="How It Works — five step workflow">
      <div className="container">
        <div className="workflow-header reveal">
          <h2 className="section-title">
            {heading ?? (
              <>
                We watch your game. <br />
                <span className="title-accent">Then we translate it.</span>
              </>
            )}
          </h2>
          <p className="section-subtitle">
            {subtitle ?? 'From strings in your repo to shippable translations in 30+ languages. Built for the producer who already knows where this process breaks.'}
          </p>
        </div>

        <div className="timeline">
          <div className="timeline__line" aria-hidden="true" />
          {WORKFLOW_STEPS.map((step, index) => (
            <div
              key={step.number}
              className={`timeline-step reveal${index % 2 === 1 ? ' timeline-step--reversed' : ''}`}
              id={`workflow-step-${step.number}`}
            >
              <div className="timeline-step__content">
                <div className="timeline-step__header">
                  <span className="timeline-step__number">{step.number}</span>
                  <span className="timeline-step__label">{step.label}</span>
                </div>
                <h3 className="timeline-step__title">{step.title}</h3>
                <p className="timeline-step__desc">{step.description}</p>
              </div>
              <div className="timeline-step__dot" aria-hidden="true" />
              <div className="timeline-step__visual">
                <StepVisual type={step.visual} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

interface PricingProps {
  heading?: string;
  subtitle?: string;
}

export function PricingSection({ heading, subtitle }: PricingProps): JSX.Element {
  return (
    <section id="pricing" className="pricing" aria-label="Pricing — pay per word per locale">
      <div className="container">
        <div className="pricing-header reveal">
          <h2 className="section-title">{heading ?? 'One price. Everything included.'}</h2>
          <p className="section-subtitle">
            {subtitle ?? 'No tiers. No hidden fees. No per-seat licensing. Quote the cost of your next patch drop in your head.'}
          </p>
        </div>

        <div className="pricing-card reveal" id="pricing-card">
          <div className="pricing-card__price-block">
            <span className="pricing-card__currency">$</span>
            <span className="pricing-card__amount">0.10</span>
            <span className="pricing-card__unit">per word per locale</span>
          </div>
          <p className="pricing-card__subtext">Flat rate. Everything below included.</p>

          <div className="pricing-card__includes">
            <div className="pricing-card__includes-col">
              <h4 className="pricing-card__includes-title">What you get for $0.10/word</h4>
              <ul className="pricing-card__includes-list">
                {PRICING_INCLUDES.map((item) => (
                  <li key={item}>
                    <span className="check-icon" aria-hidden="true">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="pricing-card__includes-col">
              <h4 className="pricing-card__includes-title">What you never pay for</h4>
              <ul className="pricing-card__includes-list pricing-card__includes-list--excludes">
                {PRICING_EXCLUDES.map((item) => (
                  <li key={item}>
                    <span className="x-icon" aria-hidden="true">×</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="pricing-card__ctas">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="cta-button cta-primary pricing-cta" id="pricing-cta">
              <span>Book a Demo</span>
            </a>
            <a href={QUOTE_MAILTO} className="cta-button cta-secondary pricing-cta" id="pricing-cta-quote">
              <span>Get a Quote</span>
            </a>
          </div>
          <p className="pricing-card__indie-note">
            Indie-ready pricing. <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">Let's talk</a>.
          </p>
        </div>
      </div>
    </section>
  );
}

interface FaqProps {
  heading?: string;
  subtitle?: string;
}

export function FaqSection({ heading, subtitle }: FaqProps): JSX.Element {
  return (
    <section id="faq" className="faq" aria-label="Frequently asked questions">
      <div className="container">
        <div className="faq-header reveal">
          <h2 className="section-title">{heading ?? 'Common questions'}</h2>
          <p className="section-subtitle">{subtitle ?? 'What producers and founder-designers ask us most.'}</p>
        </div>
        <div className="faq-list reveal">
          {FAQS.map((item) => (
            <details key={item.q} className="faq-item">
              <summary className="faq-item__q">
                <span>{item.q}</span>
                <span className="faq-item__icon" aria-hidden="true">+</span>
              </summary>
              <p className="faq-item__a">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FinalCta(): JSX.Element {
  return (
    <section className="final-cta" aria-label="Call to action — book a demo">
      <div className="container">
        <div className="final-cta__content reveal">
          <h2>Ship in every language. No producer-month lost.</h2>
          <p>
            Connect your repo, share your gameplay footage, tell us your milestone. We quote within a business day.
          </p>
          <div className="final-cta__actions">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="cta-button cta-primary" id="final-cta">
              <span>Book a Demo</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <a href={QUOTE_MAILTO} className="cta-button cta-secondary" id="final-cta-quote">
              <span>Get a Quote</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
